// assets/header-counter.js
class HeaderCounter extends HTMLElement {
  connectedCallback() {
    const endDate = this.dataset.endDate;
    const endTime = this.dataset.endTime;
    const timezone = this.dataset.timezone || 'America/New_York';
    const expiredText = this.dataset.expiredText || 'Offer Ended';

    if (!endDate || !endTime) {
      this.hidden = true;
      return;
    }

    this.endDate = endDate;
    this.endTime = endTime;
    this.timezone = timezone;
    this.expiredText = expiredText;

    this.targetDate = this.#getTargetDate(endDate, endTime, timezone);
    this.intervalId = null;

    // Start the countdown
    this.#updateCountdown();
    this.intervalId = setInterval(() => this.#updateCountdown(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * @param {string} endDate
   * @param {string} endTime
   * @param {string} timezone
   * @returns {Date}
   */
  #getTargetDate(endDate, endTime, timezone) {
    // Create date string in the specified timezone
    const dateString = `${endDate}T${endTime}:00`;
    const localDate = new Date(dateString);
    
    // For timezone conversion, we'll use the Intl API if available
    // Otherwise, we'll calculate the offset manually
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      // Get the target date/time in the specified timezone
      const parts = formatter.formatToParts(localDate);
      const yearPart = parts.find(p => p.type === 'year');
      const monthPart = parts.find(p => p.type === 'month');
      const dayPart = parts.find(p => p.type === 'day');
      const hourPart = parts.find(p => p.type === 'hour');
      const minutePart = parts.find(p => p.type === 'minute');
      
      if (!yearPart || !monthPart || !dayPart || !hourPart || !minutePart) {
        return new Date(dateString);
      }
      
      const targetDate = new Date(
        parseInt(yearPart.value, 10),
        parseInt(monthPart.value, 10) - 1,
        parseInt(dayPart.value, 10),
        parseInt(hourPart.value, 10),
        parseInt(minutePart.value, 10)
      );
      
      // Calculate offset between local time and target timezone
      const localOffset = localDate.getTimezoneOffset();
      const targetOffset = new Date(targetDate.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();
      const offsetDiff = (targetOffset - localOffset) * 60000;
      
      return new Date(targetDate.getTime() - offsetDiff);
    } catch (e) {
      // Fallback to simple date parsing
      return new Date(dateString);
    }
  }

  #updateCountdown() {
    if (!this.targetDate) return;
    
    const now = new Date();
    const diff = this.targetDate.getTime() - now.getTime();
    
    if (diff <= 0) {
      this.innerHTML = `<div class="countdown-expired">${this.expiredText}</div>`;
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      return;
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    const daysEl = this.querySelector('.days');
    const hoursEl = this.querySelector('.hours');
    const minutesEl = this.querySelector('.minutes');
    const secondsEl = this.querySelector('.seconds');
    
    if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('header-counter')) {
  customElements.define('header-counter', HeaderCounter);
}

